import { type TooltipFormatterContextObject } from 'highcharts'
import type DeviceUtil from '../utils/DeviceUtil'
import { parseDateInAOHstandard } from '../customHooks/parseDate'

interface CustomSeries extends Highcharts.Series {
  processedYData: number[]
}

export interface GraphParameters {
  kpiDefinitionIds: string[]
}

export type AssetType = keyof typeof DeviceUtil.deviceTypes

export type NETWORK_KPI = 'Signal Strength(dBm)' | 'Signal Quality(dB)' | 'Signal Power(dBm)' | 'Transmission Power(dBm)' | 'Extended Coverage Level' | 'Signal to Noise Ratio'

export const NETWORK_KPI_TO_ID: Record<NETWORK_KPI, string> = {
  'Signal Strength(dBm)': '7bffd070-348c-4096-9852-15f729c3534d',
  'Signal Quality(dB)': '8a35f202-cdf6-4775-8639-5cf23a622262',
  'Signal Power(dBm)': 'b5477914-f4dc-441b-81c1-ee09bcdd25db',
  'Transmission Power(dBm)': '3bd96dc0-dd30-4688-81fd-e2cc506176cc',
  // eslint-disable-next-line quote-props
  'Extended Coverage Level': '69319352-308f-49be-bc25-1fdf44dc5fa2',
  'Signal to Noise Ratio': '720a194f-9be4-42e8-8ed0-5f70c7b81d30'
}

export const ID_TO_NETWORK_KPI: Record<string, NETWORK_KPI> = {
  '7bffd070-348c-4096-9852-15f729c3534d': 'Signal Strength(dBm)',
  '8a35f202-cdf6-4775-8639-5cf23a622262': 'Signal Quality(dB)',
  'b5477914-f4dc-441b-81c1-ee09bcdd25db': 'Signal Power(dBm)',
  '3bd96dc0-dd30-4688-81fd-e2cc506176cc': 'Transmission Power(dBm)',
  '69319352-308f-49be-bc25-1fdf44dc5fa2': 'Extended Coverage Level',
  '720a194f-9be4-42e8-8ed0-5f70c7b81d30': 'Signal to Noise Ratio'
}

export const getGraphColorByName = (name: NETWORK_KPI): string => {
  switch (name) {
    case 'Signal Strength(dBm)':
      return '#97E7CC'
    case 'Signal Quality(dB)':
      return '#64C3FF'
    case 'Signal Power(dBm)':
      return '#F566A2'
    case 'Transmission Power(dBm)':
      return '#DA5454'
    case 'Extended Coverage Level':
      return '#EC9935'
    case 'Signal to Noise Ratio':
      return '#A58DE7'
  }
}

export const getGraphParameters = (deviceType: keyof typeof DeviceUtil.deviceTypes): GraphParameters => {
  if (deviceType === 'Water-Merlin' || deviceType === 'Agua-Merlin') {
    return {
      kpiDefinitionIds: ['7bffd070-348c-4096-9852-15f729c3534d', '8a35f202-cdf6-4775-8639-5cf23a622262', 'b5477914-f4dc-441b-81c1-ee09bcdd25db', '3bd96dc0-dd30-4688-81fd-e2cc506176cc', '69319352-308f-49be-bc25-1fdf44dc5fa2', '720a194f-9be4-42e8-8ed0-5f70c7b81d30']
    }
  } else if (deviceType === 'Water-Q600' || deviceType === 'Agua-Q600') {
    return {
      kpiDefinitionIds: ['7bffd070-348c-4096-9852-15f729c3534d', '8a35f202-cdf6-4775-8639-5cf23a622262', 'b5477914-f4dc-441b-81c1-ee09bcdd25db', '3bd96dc0-dd30-4688-81fd-e2cc506176cc', '69319352-308f-49be-bc25-1fdf44dc5fa2', '720a194f-9be4-42e8-8ed0-5f70c7b81d30']
    }
  } else if (deviceType === 'Water-HBG3' || deviceType === 'Agua-HBG3') {
    return {
      kpiDefinitionIds: ['7bffd070-348c-4096-9852-15f729c3534d', '8a35f202-cdf6-4775-8639-5cf23a622262', '720a194f-9be4-42e8-8ed0-5f70c7b81d30']
    }
  } else if (deviceType === 'Water-V200H-NBIOT' || deviceType === 'Agua-V200H-NBIOT') {
    return {
      kpiDefinitionIds: ['7bffd070-348c-4096-9852-15f729c3534d', '8a35f202-cdf6-4775-8639-5cf23a622262', 'b5477914-f4dc-441b-81c1-ee09bcdd25db', '3bd96dc0-dd30-4688-81fd-e2cc506176cc', '69319352-308f-49be-bc25-1fdf44dc5fa2', '720a194f-9be4-42e8-8ed0-5f70c7b81d30']
    }
  } else if (deviceType === 'Gas-AC250-NXS/NXU-EI8' || deviceType === 'Gas-EI6' || deviceType === 'Gas-EI7') {
    return {
      kpiDefinitionIds: ['7bffd070-348c-4096-9852-15f729c3534d', '8a35f202-cdf6-4775-8639-5cf23a622262', 'b5477914-f4dc-441b-81c1-ee09bcdd25db']
    }
  } else if (deviceType === 'Water-V200H-LoRA' || deviceType === 'Gas-EI6-LORA') {
    return {
      kpiDefinitionIds: ['7bffd070-348c-4096-9852-15f729c3534d', '720a194f-9be4-42e8-8ed0-5f70c7b81d30']
    }
  } else {
    return {
      kpiDefinitionIds: ['7bffd070-348c-4096-9852-15f729c3534d', '8a35f202-cdf6-4775-8639-5cf23a622262']
    }
  }
}

// istanbul ignore next
export function networkDetailTooltip (kpi: NETWORK_KPI): string {
  switch (kpi) {
    case 'Signal Strength(dBm)':
      return 'RSSI'
    case 'Signal Quality(dB)':
      return 'RSRQ'
    case 'Signal Power(dBm)':
      return 'RSRP'
    case 'Transmission Power(dBm)':
      return 'RSTP'
    case 'Extended Coverage Level':
      return 'ECL'
    case 'Signal to Noise Ratio':
      return 'SNR'
    default:
      return kpi
  }
}

// istanbul ignore next
export function getUnitForKpi (kpi: NETWORK_KPI): string {
  switch (kpi) {
    case 'Signal Strength(dBm)':
      return 'dBm'
    case 'Signal Quality(dB)':
      return 'dB'
    case 'Signal Power(dBm)':
      return 'dBm'
    case 'Transmission Power(dBm)':
      return 'dBm'
    case 'Extended Coverage Level':
      return 'ECL'
    case 'Signal to Noise Ratio':
      return 'dB'
    default:
      return '#F566A2'
  }
}

export const getTooltipText = (
  point: TooltipFormatterContextObject,
  _networkData: Array<{
    name: NETWORK_KPI
    color: string
    data: Array<[number, number | null]>
  }>
): string => {
  return getTooltipTextForPoint(point)
}

function getFormatedSingleTooltip (name: NETWORK_KPI, date: string, value: string): string {
  return (
    `<span style="color:${getGraphColorByName(name)}; font-size: 18px">\u25CF</span> <b>${name}</b><br>
    <span>\u25CF</span> ${date}<br>
    <span>\u25CF</span> ${networkDetailTooltip(name)}: ${value}<br>
    `
  )
}

export const getTooltipTextForPoint = (point: TooltipFormatterContextObject): string => {
  const chart = point.series.chart
  const hoveredSeriesName = point.series.name as NETWORK_KPI
  const index = point.point.index
  const dateText = parseDateInAOHstandard(point.x as string)

  // Get hovered series value
  const hoveredSeries = point.series as CustomSeries
  const hoveredValue = hoveredSeries.processedYData[index]
  const hoveredUnit = getUnitForKpi(hoveredSeriesName)

  // If no value, fallback
  if (hoveredValue === undefined || hoveredValue === null) {
    return `<b>No data available for ${hoveredSeriesName}</b>`
  }

  // Collect visible series with the same value at this index
  const matchingSeries: Array<{ name: NETWORK_KPI, value: number, unit: string }> = []

  for (const s of chart.series) {
    if (!s.visible) continue // <-- skip hidden series
    const series = s as CustomSeries
    const seriesName = s.name as NETWORK_KPI
    const seriesValue = series.processedYData[index]
    if (seriesValue === hoveredValue) {
      matchingSeries.push({
        name: seriesName,
        value: seriesValue,
        unit: getUnitForKpi(seriesName)
      })
    }
  }

  let tooltipText = ''

  if (matchingSeries.length > 1) {
    // Multiple visible series have same value - show together
    tooltipText = matchingSeries.map(d =>
      getFormatedSingleTooltip(d.name, dateText, `${d.value} ${d.unit}`)
    ).join(`
            _________________________________
            <br>`)
  } else {
    // Only hovered series
    tooltipText = getFormatedSingleTooltip(
      hoveredSeriesName,
      dateText,
      `${hoveredValue} ${hoveredUnit}`
    )
  }

  return tooltipText
}
